import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProductTable from "./ProductTable";
import AddProductModal from "./AddProductModal";
import { getMenuDetails, updateMenuDetails } from "../api/apiService";
import { Box, Button, TextField, Typography, Paper } from "@mui/material";
import Header from "./Header"; // Import the Header component

const ProductPage = ({ menuData, onUpdateMenu }) => {
  const { categoryId } = useParams(); // Get category ID from URL
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // For search box
  const storeId = localStorage.getItem("storeId");

  useEffect(() => {
    if (!storeId) {
      alert("Store ID not found. Redirecting to login.");
      navigate("/");
      return;
    }
    const category = menuData.categories.find((cat) => cat.id === categoryId);
    if (category) {
      const categoryProducts = menuData.items.filter((item) =>
        category.entities.some((entity) => entity.id === item.id)
      );
      setProducts(categoryProducts);
    }
  }, [categoryId, menuData, navigate, storeId]);

  const fetchUpdatedMenuData = async () => {
    try {
      const updatedMenuData = await getMenuDetails(storeId);
      onUpdateMenu(updatedMenuData);
      const category = updatedMenuData.categories.find((cat) => cat.id === categoryId);
      const categoryProducts = updatedMenuData.items.filter((item) =>
        category.entities.some((entity) => entity.id === item.id)
      );
      setProducts(categoryProducts);
    } catch (error) {
      console.error("Error fetching updated menu data:", error);
    }
  };

  const handleAddProduct = async (newProduct) => {
    const updatedProducts = [...menuData.items, newProduct];
    const updatedCategories = menuData.categories.map((cat) =>
      cat.id === categoryId ? { ...cat, entities: [...cat.entities, { id: newProduct.id }] } : cat
    );

    const updatedMenuData = { ...menuData, items: updatedProducts, categories: updatedCategories };

    try {
      await updateMenuDetails(storeId, updatedMenuData);
      await fetchUpdatedMenuData(); // Refresh the data after adding
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const handleEditProduct = async (updatedProduct) => {
    const updatedProducts = menuData.items.map((product) =>
      product.id === updatedProduct.id ? updatedProduct : product
    );

    const updatedMenuData = { ...menuData, items: updatedProducts };

    try {
      await updateMenuDetails(storeId, updatedMenuData);
      await fetchUpdatedMenuData(); // Refresh the data after editing
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    const updatedProducts = menuData.items.filter((product) => product.id !== productId);
    const updatedCategories = menuData.categories.map((cat) =>
      cat.id === categoryId
        ? { ...cat, entities: cat.entities.filter((entity) => entity.id !== productId) }
        : cat
    );

    const updatedMenuData = { ...menuData, items: updatedProducts, categories: updatedCategories };

    try {
      await updateMenuDetails(storeId, updatedMenuData);
      await fetchUpdatedMenuData(); // Refresh the data after deleting
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  return (
    <Box>
      <Header />
    <Box sx={{ padding: "32px" }}>
      
      <Paper sx={{ padding: "24px", borderRadius: "8px", boxShadow: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Products in Category: {categoryId}
        </Typography>

        {/* Back to Categories Button */}
        <Button
          variant="contained"
          color="inherit"
          onClick={() => navigate("/categories")}
          sx={{
            backgroundColor: "#b0b0b0",
            color: "#000",
            ":hover": {
              backgroundColor: "#9e9e9e",
            },
            marginBottom: "16px", // Add spacing below the button
          }}
        >
          Back to Categories
        </Button>

        {/* Search and Add Product Controls in Single Line */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <TextField
            label="Search Products"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="inherit"
            onClick={() => setIsAddModalOpen(true)}
            sx={{
              whiteSpace: "nowrap", // Ensures text stays on a single line
              width: "fit-content", // Button width fits text content
              paddingX: "16px", // Horizontal padding for spacing
              backgroundColor: "green",
              color: "#fff",
              ":hover": {
                backgroundColor: "#424242",
              },
            }}
          >
             Add Product
          </Button>
        </Box>

        <ProductTable
          products={products.filter((product) =>
            product.title.translations.en_us.toLowerCase().includes(searchTerm.toLowerCase())
          )}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
        />
      </Paper>
      <AddProductModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddProduct}
      />
    </Box>
    </Box>
  );
};

export default ProductPage;
